import { Box, Link, Typography } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ReactNode, useRef } from "react";

import { headingAnchor } from "./markdown_headings";
import type { HelpAsset } from "./types";

export const assetNameFromReference = (reference: string): string | null => {
  if (/^[a-z][a-z0-9+.-]*:/i.test(reference) || reference.includes("/")) {
    return null;
  }

  try {
    return decodeURIComponent(reference);
  } catch {
    return null;
  }
};

export const sanitizeHelpUrl = (url: string): string => {
  const normalized = url.trim();
  if (
    normalized.startsWith("#") ||
    (normalized.startsWith("/") && !normalized.startsWith("//")) ||
    normalized.startsWith("./") ||
    /^https?:\/\//i.test(normalized) ||
    /^mailto:/i.test(normalized)
  ) {
    return normalized;
  }
  return "";
};

const headingText = (children: ReactNode): string => {
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }
  if (Array.isArray(children)) return children.map(headingText).join("");
  if (children && typeof children === "object" && "props" in children) {
    return headingText(
      (children as { props: { children?: ReactNode } }).props.children,
    );
  }
  return "";
};

export const SafeMarkdown = ({
  markdown,
  assets,
  suppressFirstHeading = false,
  onMediaError,
}: {
  markdown: string;
  assets: HelpAsset[];
  suppressFirstHeading?: boolean;
  onMediaError?: () => void;
}) => {
  const assetsByName = new Map(assets.map((asset) => [asset.filename, asset]));
  const headingCounts = new Map<string, number>();
  let firstHeadingHandled = false;
  const mediaRefreshRequested = useRef(false);
  const requestFreshMediaUrls = () => {
    if (mediaRefreshRequested.current || !onMediaError) return;
    mediaRefreshRequested.current = true;
    onMediaError();
  };
  const uniqueHeadingAnchor = (children: ReactNode) => {
    const baseId = headingAnchor(headingText(children)) || "section";
    const count = headingCounts.get(baseId) ?? 0;
    headingCounts.set(baseId, count + 1);
    return count ? `${baseId}-${count + 1}` : baseId;
  };

  return (
    <Box
      data-testid="help-article-markdown"
      sx={{
        color: "text.primary",
        fontSize: "1rem",
        lineHeight: 1.75,
        overflowWrap: "anywhere",
        "& > :first-of-type": { mt: 0 },
        "& h1, & h2, & h3": {
          color: "atomic.ink",
          scrollMarginTop: 24,
          letterSpacing: "-0.015em",
        },
        "& h2": { mt: 5, mb: 1.5, fontSize: "1.55rem" },
        "& h3": { mt: 3.5, mb: 1, fontSize: "1.2rem" },
        "& p, & li": { color: "atomic.inkSecondary" },
        "& li": { mb: 0.75 },
        "& pre": {
          p: 2,
          borderRadius: 2,
          bgcolor: "#111827",
          color: "#F8FAFC",
          overflowX: "auto",
        },
        "& code:not(pre code)": {
          px: 0.6,
          py: 0.2,
          borderRadius: 0.75,
          bgcolor: "atomic.panelTint",
          fontSize: "0.88em",
        },
        "& blockquote": {
          ml: 0,
          pl: 2,
          borderLeft: "3px solid",
          borderColor: "atomic.brandBlue",
        },
        "& table": {
          width: "100%",
          borderCollapse: "collapse",
          my: 3,
        },
        "& th, & td": {
          p: 1.25,
          borderBottom: "1px solid",
          borderColor: "atomic.border",
          textAlign: "left",
        },
      }}
    >
      <ReactMarkdown
        skipHtml
        remarkPlugins={[remarkGfm]}
        urlTransform={(url, key) =>
          key === "src" ? url : sanitizeHelpUrl(url)
        }
        components={{
          h1: ({ children }) => {
            if (suppressFirstHeading && !firstHeadingHandled) {
              firstHeadingHandled = true;
              return null;
            }
            firstHeadingHandled = true;
            return (
              <Typography component="h1" variant="h3" sx={{ fontWeight: 700 }}>
                {children}
              </Typography>
            );
          },
          h2: ({ children }) => (
            <Typography
              component="h2"
              id={uniqueHeadingAnchor(children)}
              sx={{ fontWeight: 700 }}
            >
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography
              component="h3"
              id={uniqueHeadingAnchor(children)}
              sx={{ fontWeight: 700 }}
            >
              {children}
            </Typography>
          ),
          a: ({ href = "", children }) => {
            const external = /^https?:\/\//i.test(href);
            return (
              <Link
                href={href}
                target={external ? "_blank" : undefined}
                rel={external ? "noopener noreferrer" : undefined}
                sx={{ fontWeight: 600 }}
              >
                {children}
              </Link>
            );
          },
          img: ({ src = "", alt = "" }) => {
            const filename = assetNameFromReference(src);
            const asset = filename ? assetsByName.get(filename) : undefined;

            if (!asset) {
              return (
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    my: 2,
                    p: 2,
                    border: "1px dashed",
                    borderColor: "atomic.border",
                    borderRadius: 2,
                    color: "text.secondary",
                  }}
                >
                  Media unavailable: {filename ?? "invalid reference"}
                </Box>
              );
            }

            if (asset.mediaType === "video/mp4") {
              return (
                <Box
                  component="video"
                  src={asset.url}
                  aria-label={alt || asset.filename}
                  onError={requestFreshMediaUrls}
                  controls
                  preload="metadata"
                  sx={{
                    display: "block",
                    width: "100%",
                    maxHeight: 560,
                    my: 3,
                    borderRadius: 3,
                    bgcolor: "#111827",
                  }}
                />
              );
            }

            return (
              <Box
                component="img"
                src={asset.url}
                alt={alt}
                onError={requestFreshMediaUrls}
                loading="lazy"
                sx={{
                  display: "block",
                  width: "100%",
                  maxHeight: 640,
                  objectFit: "contain",
                  my: 3,
                  border: "1px solid",
                  borderColor: "atomic.border",
                  borderRadius: 3,
                  bgcolor: "atomic.surfaceTint",
                }}
              />
            );
          },
        }}
      >
        {markdown}
      </ReactMarkdown>
    </Box>
  );
};
